import { AddOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileItemResponse } from 'src/modules/invoice/models/invoiceTemplate';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { ProfileItem } from 'src/modules/member/models/profileItem';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllProfileGroups, getProfileGroupByIdAllItems } from 'src/shared/services/profileGroupService';

interface ProfileFilterPickerProps {
  title: string;
  values: ProfileItemResponse[];
  onChange: (values: ProfileItemResponse[]) => void;
  readOnly?: boolean;
  emptyText: string;
}

/**
 * Väljare för profilvillkor: valda profiler som chips, och ett formulär där man väljer grupp och
 * profil. Samma arbetssätt som i utskicksbeställningens urvalssteg.
 *
 * Profilerna i en grupp ingår inte i grupplistan utan hämtas när en grupp väljs, precis som i
 * utökad sök.
 */
export const ProfileFilterPicker = ({ title, values, onChange, readOnly = false, emptyText }: ProfileFilterPickerProps) => {
  const { t } = useTranslation(['common']);
  const isMounted = useIsMounted();

  const [groups, setGroups] = useState<ProfileGroup[]>([]);
  const [items, setItems] = useState<ProfileItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ProfileGroup | null>(null);
  const [selectedItem, setSelectedItem] = useState<ProfileItem | null>(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const result = await getAllProfileGroups(true, true);
        if (isMounted()) setGroups(result.data.results ?? []);
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      }
    };

    fetchGroups();
  }, [isMounted]);

  useEffect(() => {
    if (!selectedGroup) {
      setItems([]);
      return;
    }

    const fetchItems = async () => {
      setLoadingItems(true);
      try {
        const result = await getProfileGroupByIdAllItems(selectedGroup.memberProfile_id, true);
        if (isMounted()) setItems(result.data.profileItems ?? []);
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      } finally {
        if (isMounted()) setLoadingItems(false);
      }
    };

    fetchItems();
  }, [selectedGroup, isMounted]);

  // Redan valda profiler ska inte gå att lägga till en gång till.
  const availableItems = items.filter((i) => !values.some((v) => v.memberProfileItem_id === i.memberProfileItem_id));

  const toValue = (item: ProfileItem, group: ProfileGroup): ProfileItemResponse => ({
    memberProfileItem_id: item.memberProfileItem_id,
    memberProfile_id: group.memberProfile_id,
    name: item.name,
    pos: item.pos,
    groupName: group.title,
    text: '',
  });

  const closeAddForm = () => {
    setAdding(false);
    setSelectedGroup(null);
    setSelectedItem(null);
  };

  const addSelected = () => {
    if (!selectedGroup || !selectedItem) return;
    onChange([...values, toValue(selectedItem, selectedGroup)]);
    setSelectedItem(null);
  };

  const addWholeGroup = () => {
    if (!selectedGroup) return;
    onChange([...values, ...availableItems.map((i) => toValue(i, selectedGroup))]);
    setSelectedItem(null);
  };

  const remove = (itemId: number) => onChange(values.filter((v) => v.memberProfileItem_id !== itemId));

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ fontWeight: 500, mb: 1 }}>
        {title} <strong>({values.length})</strong>
      </Typography>

      {values.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {values.map((v) => (
            <Chip
              key={v.memberProfileItem_id}
              label={`${v.groupName}: ${v.name}`}
              onDelete={readOnly ? undefined : () => remove(v.memberProfileItem_id)}
              color='primary'
              variant='outlined'
            />
          ))}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          {emptyText}
        </Typography>
      )}

      {!readOnly && !adding && (
        <Button startIcon={<AddOutlined />} onClick={() => setAdding(true)}>
          {t('common:button.add')}
        </Button>
      )}

      {!readOnly && adding && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
          <Autocomplete
            size='small'
            options={groups}
            value={selectedGroup}
            onChange={(_e, value) => {
              setSelectedGroup(value);
              setSelectedItem(null);
            }}
            getOptionLabel={(g) => g.title}
            isOptionEqualToValue={(a, b) => a.memberProfile_id === b.memberProfile_id}
            renderInput={(params) => <TextField {...params} label='Profilgrupp' />}
            sx={{ minWidth: 220 }}
          />
          <Autocomplete
            size='small'
            options={availableItems}
            value={selectedItem}
            onChange={(_e, value) => setSelectedItem(value)}
            getOptionLabel={(i) => i.name}
            isOptionEqualToValue={(a, b) => a.memberProfileItem_id === b.memberProfileItem_id}
            disabled={!selectedGroup || loadingItems}
            loading={loadingItems}
            noOptionsText='Alla profiler i gruppen är redan tillagda'
            renderInput={(params) => <TextField {...params} label='Profil' />}
            sx={{ minWidth: 220 }}
          />
          <Button variant='contained' disableElevation onClick={addSelected} disabled={!selectedItem}>
            Lägg till
          </Button>
          <Button
            variant='outlined'
            disableElevation
            onClick={addWholeGroup}
            disabled={!selectedGroup || loadingItems || availableItems.length === 0}
          >
            Lägg till alla i gruppen
          </Button>
          <Button color='inherit' onClick={closeAddForm}>
            {t('common:button.cancel')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
